import { useQuery } from "@tanstack/react-query";
import type { CloudStatus, SharingState } from "@desk/contracts";
import { qk } from "./cache";
import { useDesk } from "./context";

/**
 * Where this window's account comes from: "mac" when it talks to the Mac's local API (which
 * signs in to Desk cloud itself), "cloud" when the window is signed in to Desk cloud directly.
 */
export function useAccount(): { kind: "mac" | "cloud"; status: CloudStatus | undefined; signedIn: boolean; email: string | null } {
  const { api, session } = useDesk();
  const kind = session.source === "cloud" ? "cloud" : "mac";
  const { data: status } = useQuery({
    queryKey: qk.cloud,
    queryFn: () => api.cloudStatus(),
    enabled: kind === "mac",
    refetchInterval: (q) => (q.state.data?.state === "syncing" ? 1000 : 10_000),
  });
  if (kind === "cloud") return { kind, status: undefined, signedIn: true, email: session.email ?? null };
  const signedIn = !!status && status.state !== "signed_out";
  return { kind, status, signedIn, email: status?.email ?? null };
}

/** Shared areas and invitations, once signed in to Desk cloud. */
export function useSharing() {
  const { api } = useDesk();
  const { signedIn, status } = useAccount();
  return useQuery<SharingState>({
    queryKey: qk.sharing,
    queryFn: () => api.sharing(),
    enabled: signedIn && status?.state !== "needs_choice",
    staleTime: 30_000,
  });
}
